import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTable, useFilters, useFlexLayout, useSortBy } from 'react-table';
import api from "../../../../services/api";
import {
    Label, FormGroup, Button, Modal, Container, Row, Col, UncontrolledTooltip
} from "reactstrap";
import Loader from "../../../loaders";
import { DefaultColumnFilter, NumberRangeColumnFilter } from "../../../react-table/filters";
import _ from "lodash";
import { VariableSizeList } from 'react-window';
import { TooltipButton } from '../../../inputs';
import { IndeterminateCheckbox } from '../../../react-table/controls';
import Pager from '../../../Pager';
import qs from "qs";
import { v4 as uuidV4 } from "uuid";

const CustomRow = ({ data, index, style, setSize, prepareRow }) => {
    const row = data[index];
    const rowRef = useRef();
    prepareRow(row);

    useEffect(() => {
        setSize(index, rowRef.current.getBoundingClientRect().height);
    }, [setSize, index]);

    return (
    <div
        {...row.getRowProps({ style })}
        ref={rowRef}
        style={{boxSizing: "border-box", display: "flex"}}
    >
        {row.cells.map((cell, i) => {
            return <div {...cell.getCellProps()} className={`td ${i > 3 ? "text-center" : ""}`}>{cell.render('Cell')}</div>
        })}
    </div>
    );
};

function CandidateSearch(props) {
    const {jobOrderCandidateId, jobOrderCandidateIndex, onSaved, onClose} = props;

    const [itemInSearch, setItemInSearch] = useState(null);
    const [tnaSkills, setTNASkills] = useState([]);
    const [potentialCandidates, setPotentialCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showRosteredTalents, setShowRosteredTalents] = useState(false);
    const [showAssignedTalents, setShowAssignedTalents] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [filters, setFilters] = useState([]);
    const [firstLoad, setFirstLoad] = useState(true);
    const [latestFilterId, setLatestFilterId] = useState(null);
    const skipPageResetRef = useRef();
    const listRef = useRef();
    const sizeMap = useRef({});
    const getSize = index => sizeMap.current[index] || 35;

    const setSize = useCallback((index, size) => {
      sizeMap.current = { ...sizeMap.current, [index]: size };
      listRef.current.resetAfterIndex(index);
    }, []);

    const loadData = useCallback(() => {
        skipPageResetRef.current = true;

        if (!loading)
            setLoading(true);

        const showRosteredTalentsInt = showRosteredTalents ? 1 : 0;
        const showAssignedTalentsInt = showAssignedTalents ? 1 : 0;
        const queryString = qs.stringify({ filters }, { allowDots: true });
        const apiCalls = [
            api.get(`/jobordercandidate/${jobOrderCandidateId}/tnaskills`),
            api.get(`/jobordercandidate/${jobOrderCandidateId}/search?${latestFilterId ? `searchId=${latestFilterId}&` : ""}skip=${(pageNumber - 1) * pageSize}&take=${pageSize}&showRosteredTalents=${showRosteredTalentsInt}&showAssignedTalents=${showAssignedTalentsInt}${queryString ? `&${queryString}` : ""}`)
        ];
        Promise.all(apiCalls)
            .then(([skills, candidates]) => {
                const {data: skillData} = skills;
                const {data: candidateData} = candidates;
                const checkFilterId = JSON.parse(localStorage.getItem(process.env.REACT_APP_HRIS_GRID_FILTER_STATE))?.filterId ?? null;

                if (checkFilterId !== latestFilterId)
                    return;

                setTNASkills(skillData);
                setPotentialCandidates(candidateData.data);
                setTotalRecords(candidateData.total);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            })
        ;
    }, [loading, pageNumber, pageSize, showRosteredTalents, showAssignedTalents, latestFilterId, filters]);

    const loadHeader = useCallback(() => {
        skipPageResetRef.current = true;

        if (!jobOrderCandidateId)
            return;

        setLoading(true);

        api.get(`/jobordercandidate/${jobOrderCandidateId}`)
            .then(async ({ data }) => {
                setItemInSearch(data);
                loadData();
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            })
            .finally(() => setFirstLoad(false))
        ;
    }, [jobOrderCandidateId]);

    useEffect(() => {
        if (!jobOrderCandidateId)
            return;

        loadHeader();
    }, [jobOrderCandidateId]);

    useEffect(() => {
        if (firstLoad)
            return;

        loadData();
    }, [pageNumber, pageSize, showRosteredTalents, showAssignedTalents, filters]);

    useEffect(() => {
        localStorage.setItem(process.env.REACT_APP_HRIS_GRID_FILTER_STATE, JSON.stringify({
            filterId: latestFilterId
        }));
    }, [latestFilterId]);

    const handleSave = (item) => {
        setLoading(true);
        var newItem = { ...itemInSearch, candidateId: item.id, candidateName: item.name };

        api.put(`/jobordercandidate/${itemInSearch.jobOrderCandidateId}`, JSON.stringify(newItem))
            .then((response) => {
                setLoading(false);
                onSaved(item, jobOrderCandidateIndex);
            })
            .catch((error) => {
                setLoading(false);
                console.error(error);
            });
    }

    const columns = useMemo(() => {
        const cols = [
            {
                id: "select",
                Header: "",
                minWidth: 30,
                width: 30,
                maxWidth: 30,
                Cell: ({ index, row }) => {
                    return <TooltipButton
                        id={`select_${index}`}
                        title="Select Candidate"
                        className="btn-icon"
                        color="default"
                        size="xsm"
                        type="button"
                        onClick={() => handleSave(row.original)}
                    >
                        <i className="fas fa-share"></i>
                    </TooltipButton>
                }
            },
            {
                Header: "Candidate Name",
                accessor: "name",
            },
            {
                Header: "Employed Position",
                accessor: "employedPosition",
            },
            {
                Header: "Assigned Job Order",
                accessor: "assignedJobOrder",
            },
            //{
            //    Header: "LRF",
            //    accessor: "lrfNumber",
            //},
            //{
            //    Header: "Start Date",
            //    accessor: "startDate",
            //},
            //{
            //    Header: "HRIS Position",
            //    accessor: "employedPosition",
            //},
            {
                Header: "State",
                accessor: "state",
                width: 50,
            },
            {
                Header: "Status",
                accessor: "status",
                width: 50,
            },
            {
                Header: "Score",
                accessor: "score",
                width: 100,
                Filter: (filterProps) => NumberRangeColumnFilter({...filterProps, filterNamePrefix: "score"}),
                filter: (rows, [column], [min, max]) => {
                    if (!min && !max) {
                        return rows;
                    }
                    return _.filter(rows, (row) => (!min || row.values[column] >= min) && (!max || row.values[column] <= max));
                },
            },

        ];

        if (tnaSkills && tnaSkills.length > 0) {
            tnaSkills.forEach((skill) => {
                cols.push(
                    {
                        id: skill.trainingMasterId,
                        Header: skill.trainingTitle,
                        disableSortBy: true,
                        disableFilters: true,
                        accessor: () => skill.trainingMasterId,
                        Cell: ({ row, value }) => {
                            const skills = row.original.training[value]?.skills || [];

                            return skills.length === 0 ? null : (<>
                                <div className="text-green text-center w-100 h-100 d-flex justify-content-center align-items-center" id={`tip${row.id}_${value}`}>
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <UncontrolledTooltip placement="top" target={`tip${row.id}_${value}`}>
                                    <ul className="list-unstyled mb-1 text-left">
                                        {skills.map((skill, index) => <li key={index}>{skill.name}</li>)}
                                    </ul>
                                </UncontrolledTooltip>
                            </>)
                        }
                    }
                )
            })
        }

        return cols;
    }, [tnaSkills]);

    const defaultColumn = useMemo(() => ({
        minWidth: 30,
        width: 150,
        maxWidth: 200,
        Filter: DefaultColumnFilter,
        filter: "text",
    }), []);

    const viewPortHeight = useMemo(() => window.innerHeight, []);

    const tableHeight = useMemo(() => {
        const initialHeight = viewPortHeight - 600;
        const notifBar = itemInSearch && tnaSkills.length === 0 ? 55 : 138;
        const checkBar = 40;

        const height = initialHeight + notifBar + checkBar;
        return height;
    }, [viewPortHeight, itemInSearch, tnaSkills]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        totalColumnsWidth,
        state
    } = useTable(
        {
            columns,
            data: potentialCandidates,
            defaultColumn,
            manualFilters: true,
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetRowState: !skipPageResetRef.current,
        },
        useFlexLayout,
        useFilters,
        useSortBy
    );

    useEffect(() => {
        if (!state.filters.length)
        {
            if (filters.length)
            {
                setLatestFilterId(uuidV4());
                setFilters([]);
            }

            return;
        }

        const newFilters = [];

        for (let i = 0; i < state.filters.length; i++)
        {
            if (state.filters[i].id === "score")
            {
                const minValue = state.filters[i].value[0];
                const maxValue = state.filters[i].value[1];

                if (minValue !== undefined && minValue !== null)
                    newFilters.push({
                        id: "scoreMin",
                        value: minValue
                    });

                if (maxValue !== undefined && maxValue !== null)
                    newFilters.push({
                        id: "scoreMax",
                        value: maxValue
                    });

                continue;
            }

            newFilters.push({...state.filters[i]});
        }

        setLatestFilterId(uuidV4());
        setFilters([...newFilters]);
    }, [state.filters]);

    return (
        <Modal
            isOpen={true}
            className="modal-xl m-4"
            modalClassName="modal-fullwidth"
        >
            <div
                className="modal-header"
            >
                <h5
                    className="modal-title"
                >
                    Candidate Search - {itemInSearch?.jobOrder?.hrispositionMasterName}
                </h5>
                <button
                    aria-label="Close"
                    className="close"
                    onClick={onClose}
                    type="button"
                >
                    <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                </button>
            </div>
            <div
                className="modal-body pb-0"
            >
                <Container fluid>
                    {(loading) && <Loader />}
                    {itemInSearch && tnaSkills.length === 0 &&
                        <Row className="mt-n2">
                            <Col xs={12} className="mb-3">
                                <div className="alert alert-danger" role="alert">No required / recommended skills (TNA) for this position under Project {itemInSearch?.jobOrder?.projectName} </div>
                            </Col>
                        </Row>
                    }
                    <Row className="mt-n3">

                        <Col sm={6} className="text-xs-left text-sm-right d-flex flex-wrap justify-content-start align-items-center mb-2">
                            <IndeterminateCheckbox
                                id="showAssignedTalents"
                                type="checkbox"
                                labelClassName="job-order-control mr-3"
                                onChange={() => {
                                    setShowAssignedTalents(!showAssignedTalents);
                                }}
                                checked={showAssignedTalents}
                                customTitle="Show Assigned Talents"
                            />
                            <IndeterminateCheckbox
                                id="showRosteredTalentsCheckBox"
                                type="checkbox"
                                labelClassName="job-order-control"
                                onChange={() => {
                                    setShowRosteredTalents(!showRosteredTalents);
                                }}
                                checked={showRosteredTalents}
                                customTitle="Show Rostered Talents"
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <div {...getTableProps()} className="table table-hover">
                                <div className="thead">
                                    {headerGroups.map(headerGroup => (
                                        <div {...headerGroup.getHeaderGroupProps()} className="tr">
                                            {headerGroup.headers.map((column, i) => {

                                                return (
                                                    <div {...column.getHeaderProps()} className={`th ${i > 4 ? "title-pre-wrap text-center" : ""}`}>

                                                        <span {...column.getSortByToggleProps()}>
                                                            {column.render('Header')}
                                                        </span>

                                                        <span>
                                                            {column.isSorted
                                                                ? column.isSortedDesc
                                                                    ? <i className="fas fa-long-arrow-alt-up ml-1"></i>
                                                                    : <i className="fas fa-long-arrow-alt-down ml-1"></i>
                                                                : ''}
                                                        </span>

                                                        <div className="mt-1">
                                                            {column.canFilter ? column.render('Filter') : null}
                                                        </div>

                                                    </div>
                                                )
                                            }
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div {...getTableBodyProps()} className="tbody">
                                    <VariableSizeList
                                        ref={listRef}
                                        height={tableHeight}
                                        // style={{ width: "100%" }}
                                        itemCount={rows.length}
                                        itemSize={getSize}
                                        width="100%"
                                        itemData={rows}
                                    >
                                    {({ data, index, style }) => (
                                        <div style={style}>
                                            <CustomRow
                                                data={data}
                                                index={index}
                                                setSize={setSize}
                                                prepareRow={prepareRow}
                                            />
                                        </div>
                                    )}
                                    </VariableSizeList>
                                </div>
                                <Pager
                                    totalItems={totalRecords}
                                    rowInPage={potentialCandidates.length}
                                    pageSize={pageSize}
                                    onChangePage={(pager) => setPageNumber(pager.currentPage)}
                                    onChangePageSize={(size) => setPageSize(size)}
                                    containerClassName="mt-3"
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>

            </div>
            <div
                className="modal-footer"
            >
                <Button
                    color="secondary"
                    onClick={onClose}
                    type="button"
                >
                    Close
                </Button>
            </div>

        </Modal>
    )

}
export default CandidateSearch;